// PowerSync logout cleanup.
//
// Clears the local SQLite database on logout for PII hygiene. Field
// staff may use shared devices; clearing on sign-out ensures one
// user's capture data is not visible to the next user on the same
// device before the next sync.
//
// disconnectAndClear({ clearLocal: true }) does four things:
//   1. Disconnects the sync WebSocket
//   2. Drops all synced table data from local SQLite
//   3. Clears the ps_crud upload queue
//   4. Clears local-only tables (the clearLocal: true flag)
//
// Steps 3 and 4 are DESTRUCTIVE to un-uploaded work: step 3 discards
// queued capture writes that never reached the server, and step 4 wipes
// the `orphaned_capture_items` quarantine — counts the connector held on
// the device precisely because they could not sync. Both are contractual
// asset counts that live ONLY on this device, so clearing them on
// sign-out is a silent data-loss path (audit-2026-06-22 C4 / #696, folds
// H10). `getHeldCaptureLoad()` below lets the logout flow detect that
// at-risk work and warn BEFORE the clear, so the surveyor never destroys
// held counts without an explicit, loud confirmation.
//
// This must be called BEFORE the form-submit logout (submitLogout()
// in hooks/api.ts) so the DB is cleared while the session cookie is
// still present. After submitLogout() the browser navigates away and
// the async clear would be abandoned.
//
// Wire-up: auth-provider.tsx :: logout() calls this before
// submitLogout(). See apps/frontend/src/app/auth-provider.tsx.
//
// See docs/plans/research/powersync-frontend-api.md §9 for the
// official logout pattern.

import { withEnqueueSignal } from '../../capture/client/capture-client-powersync-enqueue-signal'
import { apiFetch } from '../api'

import { db } from './client'
import { reinjectStash, stashHeldWork } from './held-work-offline-stash'
import { createIndexedDbStashStore } from './indexed-db-stash-store'
import {
  requeueStashedCounts,
  type CaptureItemsWriteDb,
} from './requeue-stashed-counts'
import {
  collectHeldWork,
  rescueHeldCaptures,
  rescueUploadQueue,
  type ReadableDb,
  type RecoveryDumpItem,
  type RecoveryDumpResponse,
  type RescueResult,
  type UploadQueueReadableDb,
} from './rescue-held-captures'

/**
 * Disconnects PowerSync and clears the local SQLite mirror.
 * Call this before triggering the session-cookie logout flow.
 */
export async function clearPowerSyncLocalDb(): Promise<void> {
  await db.disconnectAndClear({ clearLocal: true })
}

/**
 * Ship the device-held quarantine (`orphaned_capture_items`) to the durable
 * server recovery store BEFORE `clearPowerSyncLocalDb()` wipes it on sign-out.
 *
 * This is the "wire recovery before any clear" half of the H8/H9 fix
 * (audit-2026-06-22 / #696, #663): the C4 guard already BLOCKS a silent
 * sign-out while held counts exist, but a surveyor who confirms the discard
 * would still lose those counts because recovery was a hidden pilot-only route
 * (`/recover-held-captures`). Calling this in the logout flow makes recovery
 * real at the exact moment of risk: the quarantined counts land in the isolated
 * server inbox (`POST /capture/recovery-dump`) and survive the local clear.
 *
 * Mechanics mirror the pilot page (`recover-held-captures-page.tsx`): it READS
 * `orphaned_capture_items` (never deletes the on-device rows — the clear that
 * follows removes them) and writes ONLY to the isolated inbox; it never touches
 * live `capture_items`, the dashboard, the CSV export, or sync. Idempotent — the
 * server upserts on (user, original_item_id), so a re-run (e.g. a retried
 * sign-out) stores the same rows once. An empty quarantine is a no-op success
 * (no POST). Resolves with how many counts were found vs. stored; the caller
 * (auth-provider.tsx) bounds and audits it, and proceeds with the clear whether
 * it succeeds or fails — a wedged worker / offline device must never trap a
 * surveyor on a shared device, and the unshipped remainder is recorded, not
 * dropped unseen.
 *
 * NOTE — covers the QUARANTINE dimension only. The `ps_crud` upload queue
 * (un-uploaded but still-syncable writes, the H10 dimension) is PowerSync
 * internal and is not reshaped into inbox rows here; the guard warns about it
 * and the caller's audit records its discard.
 */
export async function preserveHeldCapturesBeforeClear(): Promise<RescueResult> {
  // Adapt the PowerSync singleton to the rescue core's minimal ReadableDb via an
  // explicit forward (no cast — keeps the escape-hatch lint clean), matching the
  // pilot page's adapter.
  const readableDb: ReadableDb = {
    getAll: (sql, params) => db.getAll<Record<string, unknown>>(sql, params),
  }
  return rescueHeldCaptures(readableDb, postRecoveryDump)
}

function postRecoveryDump(
  items: RecoveryDumpItem[],
): Promise<RecoveryDumpResponse> {
  return apiFetch<RecoveryDumpResponse>('/capture/recovery-dump', {
    method: 'POST',
    json: { items },
  })
}

/**
 * F3 (#809), TIER 2 — ship the device's ps_crud-QUEUED capture_items counts to
 * the durable server recovery inbox BEFORE `clearPowerSyncLocalDb()` wipes the
 * queue. The quarantine rescue above covers only `orphaned_capture_items`; this
 * covers the un-uploaded-but-syncable queue (the "H10" dimension — Sentry `1P`,
 * 7,119 ops discarded on one logout). Read-only on the device (it reads the
 * pending CRUD batch and the live rows, NEVER calls `complete()` so it cannot
 * drain ps_crud out from under a still-online upload) and writes ONLY to the
 * isolated inbox — same safety envelope and idempotency key (the capture_items
 * id) as the quarantine rescue, so a row that ALSO uploads normally is harmless.
 * The caller bounds + audits it and proceeds with the clear whether it succeeds
 * or fails — a wedged worker must never trap a surveyor on a shared device.
 */
export async function preserveUploadQueueBeforeClear(): Promise<RescueResult> {
  return rescueUploadQueue(queueReader(), postRecoveryDump)
}

/**
 * The device's read-only view for the queue/quarantine rescues: `getAll` over the
 * live SQLite tables plus a `getCrudBatch` that normalises the SDK CrudBatch to
 * the rescue's minimal op/table/id shape. The rescues NEVER call the batch's
 * `complete()`, so reading through this never drains ps_crud. Shared by the Tier-2
 * online queue rescue and the Tier-3 offline-logout stash so both observe the
 * identical device state.
 */
function queueReader(): UploadQueueReadableDb {
  return {
    getAll: (sql, params) => db.getAll<Record<string, unknown>>(sql, params),
    getCrudBatch: async (limit) => {
      const batch = await db.getCrudBatch(limit)
      return batch === null
        ? null
        : {
            crud: batch.crud.map((e) => ({
              op: String(e.op),
              table: e.table,
              id: e.id,
              // Carry the op payload + trackMetadata snapshot so the queue rescue
              // can preserve a count whose live row was already sync-deleted (the
              // bare-row case): opData holds the count, metadata the FK context the
              // increment snapshotted. Without these the rescue could only ship
              // queued counts that still join a live row. (Codex P1)
              opData: e.opData ?? null,
              metadata: e.metadata,
            })),
            haveMore: batch.haveMore,
          }
    },
  }
}

/**
 * F3 (#809), TIER 3 — OFFLINE-logout durable stash. Tiers 1+2 ship held work to
 * the server inbox before the clear, but that POST needs the network: an OFFLINE
 * sign-out can't reach the server, so the clear would destroy the un-uploaded
 * counts. This stashes ALL of the device's held work (the `orphaned_capture_items`
 * quarantine AND the ps_crud queue) into durable on-device IndexedDB that SURVIVES
 * `disconnectAndClear`, KEYED to the logging-out `userId`, so a later on-login
 * re-inject (`reinjectStashedHeldWork`) ships it once the device is back online —
 * and ONLY for that same user (shared-device safety; see held-work-offline-
 * stash.ts). Read-only on the device (never drains ps_crud); the subsequent clear
 * still runs. Best-effort, resolves with the number of counts stashed (0 when the
 * device held nothing — then no slot is written). The caller invokes this only
 * when the device is OFFLINE: online, Tier-2 already shipped the work, so a stash
 * would be a redundant on-device copy of already-durable counts.
 */
export async function stashHeldWorkOnLogout(
  userId: string,
  stampedAt: string,
): Promise<{ stashed: number; truncated: boolean }> {
  const bundle = await collectHeldWork(queueReader())
  await stashHeldWork(createIndexedDbStashStore(), userId, bundle, stampedAt)
  return {
    stashed: bundle.queueItems.length + bundle.quarantineItems.length,
    // The pending queue exceeded the read ceiling — an unread overflow is at risk of
    // being wiped by the clear that follows. The caller audits this loudly (Codex :316).
    truncated: bundle.truncated === true,
  }
}

/** The PowerSync singleton adapted to the re-queue's minimal write seam — an
 *  explicit forward (no cast, keeps the escape-hatch lint clean), matching how the
 *  rescue adapters forward `getAll`. The re-queue INSERTs into the local
 *  capture_items view, which PowerSync turns into a ps_crud PUT the connector
 *  uploads — identical to a live capture write.
 *
 *  Routed through `withEnqueueSignal` so each ps_crud-queuing INSERT/UPDATE emits the
 *  `_emitEnqueued()` pulse, exactly like a live capture write. Without it,
 *  `useUnsyncedCaptureCount` (which disarms its poll on an online confirmed-zero device
 *  and relies on the pulse for newly-queued local writes) could keep showing "all saved"
 *  while a re-injected backlog is still pending upload (Codex — emit enqueue events for
 *  requeued stash writes). */
function captureItemsWriter(): CaptureItemsWriteDb {
  const signalled = withEnqueueSignal(db)
  return {
    execute: (sql, params) => signalled.execute(sql, params),
    getAll: (sql, params) => signalled.getAll<Record<string, unknown>>(sql, params),
  }
}

/**
 * F3 (#809), TIER 3 — on-login re-inject of the offline-logout stash, wired to the
 * production IndexedDB store, the real PowerSync write path, and the real
 * recovery-dump POST. Thin: the testable orchestration (read this user's slot →
 * route each dimension → clear-only-after-confirmed-handling, idempotent +
 * shared-device-scoped) lives in `reinjectStash` (held-work-offline-stash.ts).
 *   - queueItems      → re-INSERT into the live capture path (requeueStashedCounts)
 *     so they re-enter ps_crud and the upload queue DRAINS on the device — the fix
 *     that brings the surveyor's offline counts back instead of stranding them in
 *     the inbox.
 *   - quarantineItems → POST to the recovery inbox (the established quarantine path).
 * Re-injects only the logging-back-in user's own stash; throws only if a sink fails
 * (the caller surfaces/swallows), leaving the un-handled remainder to retry.
 */
export async function reinjectStashedHeldWork(
  userId: string,
): Promise<RescueResult> {
  return reinjectStash(
    createIndexedDbStashStore(),
    postRecoveryDump,
    (items) => requeueStashedCounts(captureItemsWriter(), items),
    userId,
  )
}

/**
 * F3 (#809), TIER 1 (best-effort) — give an ONLINE upload queue a BOUNDED window
 * to drain naturally (the connector uploads in the background) before the Tier-2
 * rescue ships whatever remains, so a healthy online sign-out leaves the rescue a
 * no-op. This is NOT a durability guarantee: a wedged / offline queue won't
 * drain, which is exactly why Tier-2 is the actual safety net. Polls
 * `getUploadQueueStats`; resolves when the queue empties or the bound elapses.
 * Never throws — a read error or a missing stat method just ends the wait early
 * (Tier-2 still runs). Off-test the method is always present; under the jsdom
 * stub it is absent, so the wait is a no-op (the rescue is what the tests pin).
 */
export async function waitForUploadQueueToSettle(
  timeoutMs: number,
  pollMs = 250,
): Promise<void> {
  const getStats = (db as UploadQueueProbe).getUploadQueueStats
  if (typeof getStats !== 'function') return
  const maxPolls = Math.max(1, Math.ceil(timeoutMs / pollMs))
  for (let i = 0; i < maxPolls; i += 1) {
    try {
      const stats = await getStats.call(db, false)
      if (toCount(stats?.count) === 0) return
    } catch {
      return
    }
    await new Promise<void>((resolve) => setTimeout(resolve, pollMs))
  }
}

/**
 * Result of probing the device for un-uploaded capture work that
 * `clearPowerSyncLocalDb()` would destroy on sign-out.
 *
 * `known` carries exact counts:
 *   - `quarantineCount`  — rows in the device-local `orphaned_capture_items`
 *     table: counts the connector preserved because they can never sync
 *     as-is and there is (pre-#663) no recovery surface. Wiped by step 4.
 *   - `uploadQueueCount` — entries in the PowerSync `ps_crud` upload queue:
 *     capture writes that have not yet been acknowledged by the server.
 *     Wiped by step 3.
 *
 * `indeterminate` means at least one probe could not be read (the local DB
 * was locked / initializing, or the read threw). We must NOT treat that as
 * "nothing held" — that would let the clear destroy the only copy of a
 * preserved count without warning — so an indeterminate result fails CLOSED
 * (it warns), the safe direction for a loss-prevention guard.
 */
export type HeldCaptureLoad =
  | {
      readonly status: 'known'
      readonly quarantineCount: number
      readonly uploadQueueCount: number
    }
  | { readonly status: 'indeterminate' }

/** A dimension read either yields a count or fails (unknown — NOT zero). */
type CountOrError = number | 'error'

/** PowerSync exposes `getUploadQueueStats` on the real engine but not on the
 *  jsdom test stub; type it as optional so we feature-detect rather than
 *  assume it. NOTE: unlike `connector-quarantine.ts`'s `getOptional` (which
 *  fails OPEN — a missing optional is benign enrichment), this feature-detect
 *  fails CLOSED off-test: a missing method on a real engine is SDK drift →
 *  `indeterminate`, never a silent 0 (see `countUploadQueue`; PR #743 / #12). */
interface UploadQueueProbe {
  getUploadQueueStats?: (
    includeSize?: boolean,
  ) => Promise<{ count: number } | null | undefined>
}

/**
 * Inspect the device for un-uploaded capture work a logout clear would
 * destroy.
 *
 * Read ORDER matters (race-safety): the connector quarantines a failed
 * upload by moving it queue → quarantine (insert into orphaned_capture_items,
 * then `batch.complete()` drains the `ps_crud` entry). Reading quarantine
 * first then queue could miss a count migrating in that window (absent from
 * quarantine when read, then drained from the queue before the queue read).
 * Reading the QUEUE FIRST closes that: a count is in the queue OR has already
 * landed in quarantine (which only grows until recovery/logout), so it is
 * caught by one of the two reads — at worst counted twice for an instant
 * (a safe over-count, never a miss).
 *
 * Failure handling fails CLOSED: if either probe throws (DB locked /
 * initializing), the result is `indeterminate` rather than a false zero, so
 * the clear can't silently destroy a preserved count. A missing
 * getUploadQueueStats counts as a known 0 ONLY under test (the jsdom stub lacks
 * it); on a real engine its absence is treated as a read error → indeterminate,
 * because a real SDK always has it and a silent 0 would be a false zero
 * (#732 / #12).
 *
 * COALESCE GAP — CLOSED by #726: this still reads only SQLite + the ps_crud
 * queue, and a capture `+` tap briefly sits in the capture client's in-memory
 * 500 ms coalesce (capture-client-powersync.ts) before it is written to
 * SQLite/ps_crud. That window is now handled OUTSIDE this module: the logout
 * flow (auth-provider.tsx :: logout()) awaits a bounded
 * flushAllCaptureClients() — draining every mounted client's coalesce to
 * ps_crud — BEFORE it calls this probe, and fails closed to the loud dialog if
 * that flush times out or rejects. So by the time getHeldCaptureLoad() runs,
 * the tap has been persisted and IS counted here. This function's contract
 * (read ps_crud + quarantine; fail closed on a read error) is deliberately
 * unchanged — the fix is the pre-flush in the caller, not a new dimension here.
 */
export async function getHeldCaptureLoad(): Promise<HeldCaptureLoad> {
  const uploadQueueCount = await countUploadQueue()
  const quarantineCount = await countQuarantinedItems()
  if (uploadQueueCount === 'error' || quarantineCount === 'error') {
    return { status: 'indeterminate' }
  }
  return { status: 'known', quarantineCount, uploadQueueCount }
}

/**
 * The single definition of "how many changes are held on this device": the sum
 * of the two known dimensions, or `null` when the device read was indeterminate.
 * The two dimensions are disjoint by construction (a count is queued OR
 * quarantined — `getHeldCaptureLoad`'s queue-first read order guarantees it is
 * caught by exactly one), so summing is EXACT, never a double-count. During the
 * brief queue→quarantine migration window a single count could transiently be
 * read in BOTH, making the sum an over-count for an instant — that is the SAFE
 * direction (it over-warns, never under-warns) and self-corrects on the next
 * probe. The sum (not `Math.max`) is the documented semantics: a "dedupe" mis-fix
 * to `Math.max(q, u)` would under-report and is caught by the exact-sum test.
 *
 * Every consumer that displays or tests the held total MUST derive it from here
 * rather than re-summing the dimensions itself, so the logout dialog, the
 * capture banner, and the `hasHeldCaptureLoad` predicate can never disagree
 * about the NUMBER or NOUN on a given device state (audit PR #732 / #13). They
 * diverge only on the indeterminate read's fail-direction — see the banner /
 * dialog (banner hides, dialog warns).
 */
export function heldCaptureTotal(load: HeldCaptureLoad): number | null {
  return load.status === 'known'
    ? load.quarantineCount + load.uploadQueueCount
    : null
}

/**
 * The umbrella noun for held (un-uploaded) work, with singular/plural agreement.
 *
 * It is "change(s)", NOT "item(s)": the quarantine dimension is always captured
 * counts, but the `ps_crud` queue dimension can also hold non-count writes (room
 * renames, session completes), so "changes" is the accurate umbrella for the
 * summed total. "items" would read as capture items / asset counts and mislabel
 * the quantity on a contractual surface. Both the logout dialog and the capture
 * banner consume this so the two surfaces describe the same number identically
 * (audit PR #732 / #1).
 */
export function heldChangeNoun(count: number): 'change' | 'changes' {
  return count === 1 ? 'change' : 'changes'
}

/**
 * True when the device may be holding capture work the clear would destroy.
 * Indeterminate fails CLOSED (true) — better to warn on an unverifiable
 * device than to silently wipe a preserved count.
 *
 * Defined in terms of `heldCaptureTotal` so "what counts as held" lives in one
 * place: `null` (indeterminate) fails closed to held; a known total is held
 * when it is positive. Equivalent to the prior `q > 0 || u > 0` because both
 * dimensions are non-negative COUNT reads.
 */
export function hasHeldCaptureLoad(load: HeldCaptureLoad): boolean {
  const total = heldCaptureTotal(load)
  return total === null || total > 0
}

/**
 * Coerce a COUNT/stat value to a finite non-negative-ish number, defaulting to
 * 0 on anything unusable. Coerces stringy values (some drivers return aggregate
 * results as strings) rather than discarding them — for a loss-prevention check
 * the dangerous direction is UNDER-counting (missing held work), so we favour
 * recovering a real number over a bare 0. Shared by both dimensions so they
 * behave identically.
 */
function toCount(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const coerced = Number(value)
  return Number.isFinite(coerced) ? coerced : 0
}

async function countQuarantinedItems(): Promise<CountOrError> {
  try {
    const rows = await db.getAll<{ n: number }>(
      'SELECT COUNT(*) AS n FROM orphaned_capture_items',
    )
    return toCount(rows[0]?.n)
  } catch {
    // Fail closed: a failed read is "unknown", not "empty". (Codex P1)
    return 'error'
  }
}

async function countUploadQueue(): Promise<CountOrError> {
  const getStats = (db as UploadQueueProbe).getUploadQueueStats
  if (typeof getStats !== 'function') {
    // The jsdom @powersync stub legitimately lacks getUploadQueueStats, so under
    // test its absence is a known 0. On a REAL engine (any non-test build) the
    // method is always present — if it goes missing the SDK shape has drifted
    // (renamed/removed/moved), and returning 0 would be a SILENT false zero: the
    // logout guard would skip its warning and the held-work banner would show
    // "all synced" while the ps_crud queue is full. That is the exact false zero
    // this module exists to prevent, re-entering through a duck-typed seam that
    // fails QUIET instead of LOUD. Fail CLOSED to indeterminate off-test,
    // matching the module's fail-closed doctrine (#732 / #12).
    return import.meta.env.MODE === 'test' ? 0 : 'error'
  }
  try {
    const stats = await getStats.call(db, false)
    return toCount(stats?.count)
  } catch {
    return 'error'
  }
}
